/**
 * MilestoneList — pretty list of earned milestones for a profile.
 * Resolves icon names from the lookup table to lucide components.
 */
import {
  Award,
  Calendar,
  Flame,
  GraduationCap,
  Heart,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useMilestones, type EarnedMilestone } from "@/lib/milestones/useMilestones";

const ICONS: Record<string, LucideIcon> = {
  award: Award,
  calendar: Calendar,
  flame: Flame,
  "graduation-cap": GraduationCap,
  heart: Heart,
  "shield-check": ShieldCheck,
  sparkles: Sparkles,
};

const MilestoneChip = ({ m }: { m: EarnedMilestone }) => {
  const Icon = ICONS[m.icon] ?? Award;
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/40 px-3 py-1.5 text-xs"
      title={m.description}
    >
      <Icon className="h-3.5 w-3.5 text-primary" aria-hidden />
      <span className="font-medium">{m.label}</span>
    </div>
  );
};

interface Props {
  userId: string | null | undefined;
  emptyHint?: string;
}

const MilestoneList = ({ userId, emptyHint }: Props) => {
  const { items, loading } = useMilestones(userId);

  if (loading) {
    return <p className="text-xs text-muted-foreground">Loading milestones…</p>;
  }
  if (items.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        {emptyHint ?? "No milestones yet. Keep going."}
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((m) => (
        <MilestoneChip key={m.key} m={m} />
      ))}
    </div>
  );
};

export default MilestoneList;
